// BlockNote waits ~10ms, ~100ms, or ~200ms between script steps. Chrome stretches
// those timers while this tab is hidden, so the same stream sits still until
// the tab is focused again. Each wait still runs as its own turn. A microtask
// applies the next edit before the editor has finished the previous one.
function isTypingDelay(delay: number) {
  return (
    (delay >= 8 && delay <= 12) ||
    (delay >= 80 && delay <= 120) ||
    (delay >= 160 && delay <= 240)
  );
}

export function installBackgroundGenerationClock() {
  const originalSetTimeout = window.setTimeout.bind(window);
  const originalClearTimeout = window.clearTimeout.bind(window);
  const pending = new Map<number, () => void>();
  const channel = new MessageChannel();
  let nextId = 1_000_000_000;

  channel.port1.onmessage = (event: MessageEvent<number>) => {
    const scheduled = pending.get(event.data);
    if (!scheduled) return;
    pending.delete(event.data);
    scheduled();
  };

  window.setTimeout = ((
    handler: TimerHandler,
    timeout?: number,
    ...args: unknown[]
  ) => {
    const delay = typeof timeout === "number" ? timeout : 0;
    if (!document.hidden || !isTypingDelay(delay) || typeof handler !== "function") {
      return originalSetTimeout(handler, timeout, ...args);
    }
    const id = nextId;
    nextId += 1;
    pending.set(id, () => {
      handler(...args);
    });
    channel.port2.postMessage(id);
    return id;
  }) as typeof window.setTimeout;

  window.clearTimeout = ((id?: number) => {
    if (typeof id === "number" && pending.delete(id)) return;
    originalClearTimeout(id);
  }) as typeof window.clearTimeout;

  return () => {
    pending.clear();
    channel.port1.onmessage = null;
    channel.port1.close();
    channel.port2.close();
    window.setTimeout = originalSetTimeout;
    window.clearTimeout = originalClearTimeout;
  };
}
